import React, { useState, useMemo } from 'react';
import useSWR from 'swr';
import { 
  TrendingUp,
  TrendingDown,
  Calendar,
  DollarSign,
  Users,
  Clock,
  AlertCircle,
  Plus,
  ChevronRight,
  ChevronLeft,
  BarChart3,
  CheckCircle2,
  Download,
  FileJson,
  FileSpreadsheet,
  FileText,
  Wallet,
  RotateCcw,
  X,
  MessageSquare,
  Mail,
  Phone,
  UserCircle,
  Copy,
  Loader2
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import TabPageLayout, { PageLoader } from './TabPageLayout';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import api from '../utils/api';
import { apiV2 } from '../utils/apiV2';
import { toast } from 'sonner';

// SWR fetcher - uses v2 dashboard endpoint (PostgreSQL)
const fetcher = async (url) => {
  try {
    // Use v2 dashboard endpoint (PostgreSQL-based)
    const v2Url = url.replace('/dashboard/', '/v2/dashboard/');
    const response = await api.get(v2Url);
    return response.data;
  } catch (error) {
    console.error('Dashboard stats error:', error);
    // Return default stats to prevent UI crash
    return {
      revenue: { total: 0, delivered: 0, projected: 0, trend: 0 },
      bookings: { total: 0, completed: 0, pending: 0, confirmed: 0 },
      avgBookingValue: 0,
      leads: { total: 0 },
      upcomingBookings: [],
      recentBookings: [],
      chartData: [],
      sources: [],
      services: 0
    };
  }
};

// Leads fetcher - fetches paginated leads for dashboard
const leadsFetcher = async (url) => {
  try {
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Leads fetch error:', error);
    return { leads: [], pagination: { page: 1, limit: 10, total: 0, totalPages: 1, hasMore: false } };
  }
};

// Chart colors
const COLORS = ['#3b82f6', '#94a3b8'];

// Helper function outside component
const formatTime = (time) => {
  if (!time) return '';
  try {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  } catch {
    return time;
  }
};

// TrendIndicator component - only shows when there's valid comparison data
const TrendIndicator = ({ value, previous, suffix = '%' }) => {
  // Don't show trend if:
  // - No value or value is 0
  // - No previous data to compare against
  // - The trend calculation would be meaningless (dividing by 0)
  if (value === null || value === undefined || !previous || previous === 0) {
    return null;
  }
  
  if (value === 0) return null;
  
  const isPositive = value > 0;
  return (
    <span className={`flex items-center gap-0.5 text-xs font-medium ${isPositive ? 'text-green-600' : 'text-red-500'}`}>
      {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {Math.abs(value).toFixed(0)}{suffix}
    </span>
  );
};

// Custom tooltip for bar chart - shows completed and projected separately
const CustomTooltip = ({ active, payload, label, currencySymbol = '$', isDark = false }) => {
  if (active && payload && payload.length) {
    const data = payload[0]?.payload || {};
    const completed = data.completed || 0;
    const projected = data.projected || 0;
    const total = completed + projected;
    const completedBookings = data.completedBookings || data.bookings || 0;
    const projectedBookings = data.projectedBookings || 0;
    const isCurrentMonth = data.isCurrentMonth;
    
    return (
      <div className="bg-white shadow-[0_8px_32px_rgba(0,0,0,0.12)] rounded-xl p-3 text-sm min-w-[140px]">
        <p className={`font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{label}</p>
        {completed > 0 && (
          <div className="flex items-center justify-between gap-3 mb-1">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: '#2563EB' }}></span>
              <span className="text-xs text-gray-600">Completed</span>
            </span>
            <span className="font-semibold" style={{ color: '#2563EB' }}>{currencySymbol}{completed.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
          </div>
        )}
        {projected > 0 && (
          <div className="flex items-center justify-between gap-3 mb-1">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: '#93b4f7' }}></span>
              <span className="text-xs text-gray-600">Collected</span>
            </span>
            <span className="font-semibold" style={{ color: '#93b4f7' }}>{currencySymbol}{projected.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
          </div>
        )}
        {(completed > 0 || projected > 0) && (
          <div className={`pt-1 mt-1 border-t flex items-center justify-between ${isDark ? 'border-slate-600' : 'border-gray-100'}`}>
            <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Total</span>
            <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{currencySymbol}{total.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
          </div>
        )}
        {total === 0 && (
          <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>No revenue</p>
        )}
      </div>
    );
  }
  return null;
};

// Custom shape for completed bar - rounded top only when no projected data stacked on top
const CompletedBarShape = (props) => {
  const { x, y, width, height, payload } = props;
  const hasProjected = (payload?.projected || 0) > 0;
  const radius = 4;
  
  if (!height || height <= 0) return null;
  
  // If there's projected data stacked on top, no rounded corners on completed
  if (hasProjected) {
    return <rect x={x} y={y} width={width} height={height} fill="#2563EB" />;
  }
  
  // No projected data - add rounded top corners
  return (
    <path
      d={`
        M ${x},${y + height}
        L ${x},${y + radius}
        Q ${x},${y} ${x + radius},${y}
        L ${x + width - radius},${y}
        Q ${x + width},${y} ${x + width},${y + radius}
        L ${x + width},${y + height}
        Z
      `}
      fill="#2563EB"
    />
  );
};

// Custom X-axis tick to show "today/this week/this month" indicator
// Responsive: smaller text on mobile, first letter only for many months
const CustomXAxisTick = ({ x, y, payload, granularity, isMobile, dataLength, startDate, endDate, isDark = false }) => {
  let isCurrent = false;
  const today = new Date();
  
  // Check if today falls within the selected date range
  const rangeStart = startDate ? new Date(startDate) : null;
  const rangeEnd = endDate ? new Date(endDate) : null;
  const todayInRange = rangeStart && rangeEnd && today >= rangeStart && today <= rangeEnd;
  
  if (todayInRange) {
    if (granularity === 'daily') {
      // Check if viewing current day
      const todayDayName = today.toLocaleDateString('en-US', { weekday: 'short' });
      const todayDate = today.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      isCurrent = payload?.value === todayDayName || payload?.value === todayDate;
    } else if (granularity === 'weekly') {
      // Don't highlight weeks - too complex to determine "current week"
      isCurrent = false;
    } else if (granularity === 'monthly') {
      // Only highlight if current month label matches
      const currentMonthLabel = today.toLocaleDateString('en-US', { month: 'short' });
      isCurrent = payload?.value === currentMonthLabel;
    } else if (granularity === 'yearly') {
      const currentYear = today.getFullYear().toString();
      isCurrent = payload?.value === currentYear;
    }
  }
  
  // Smaller font on mobile, use first letter for months if 12+ data points on mobile
  const fontSize = isMobile ? 9 : 11;
  const dotSize = isMobile ? 2 : 3;
  const dotY = isMobile ? 18 : 22;
  
  // Use first letter only for monthly view with 12+ months on mobile
  let displayValue = payload?.value;
  if (isMobile && granularity === 'monthly' && dataLength >= 12) {
    // Get first letter: Jan -> J, Feb -> F, etc.
    displayValue = payload?.value?.charAt(0) || payload?.value;
  }
  
  // Colors based on dark mode
  const normalColor = isDark ? '#94a3b8' : '#6b7280';  // slate-400 vs gray-500
  const currentColor = '#3b82f6';  // blue-500
  
  return (
    <g transform={`translate(${x},${y})`}>
      <text 
        x={0} 
        y={0} 
        dy={10} 
        textAnchor="middle" 
        fill={isCurrent ? currentColor : normalColor}
        fontSize={fontSize}
        fontWeight={isCurrent ? 600 : 400}
      >
        {displayValue}
      </text>
      {isCurrent && (
        <circle cx={0} cy={dotY} r={dotSize} fill={currentColor} />
      )}
    </g>
  );
};

const OperationsDashboard = ({ onNavigate }) => {
  // Default to current calendar year (Jan 1 - Dec 31)
  const getDefaultDates = () => {
    const today = new Date();
    const year = today.getFullYear();
    
    return {
      start: `${year}-01-01`,
      end: `${year}-12-31`
    };
  };
  
  // Memoize default dates so they don't change on every render
  const defaultDates = useMemo(() => getDefaultDates(), []);
  const [startDate, setStartDate] = useState(defaultDates.start);
  const [endDate, setEndDate] = useState(defaultDates.end);
  
  // Check if dates are at default (for showing reset button)
  const isDefaultDateRange = startDate === defaultDates.start && endDate === defaultDates.end;
  
  // Reset to default date range
  const resetDateRange = () => {
    const defaults = getDefaultDates();
    setStartDate(defaults.start);
    setEndDate(defaults.end);
  };
  
  // Date limits: 5 years ago to 6 months forward
  const getDateLimits = () => {
    const today = new Date();
    const fiveYearsAgo = new Date(today);
    fiveYearsAgo.setFullYear(fiveYearsAgo.getFullYear() - 5);
    const sixMonthsForward = new Date(today);
    sixMonthsForward.setMonth(sixMonthsForward.getMonth() + 6);
    
    return {
      minDate: fiveYearsAgo.toISOString().split('T')[0],
      maxDate: sixMonthsForward.toISOString().split('T')[0]
    };
  };
  
  const dateLimits = getDateLimits();

  // Build the API URL with custom dates
  const getApiUrl = () => {
    return `/dashboard/stats?period=custom&startDate=${startDate}&endDate=${endDate}`;
  };

  // useSWR for data fetching - auto revalidates on focus and every 60 seconds
  const { data: stats, error, isLoading, isValidating } = useSWR(
    startDate && endDate ? getApiUrl() : null,
    fetcher,
    {
      revalidateOnFocus: true,
      refreshInterval: 60000, // Refresh every 60 seconds (was 30s)
      dedupingInterval: 10000, // Dedupe requests within 10 seconds
      keepPreviousData: true, // Keep showing old data while fetching new (prevents flash)
    }
  );
  
  // Leads pagination state
  const [leadsPage, setLeadsPage] = useState(1);
  const [selectedLead, setSelectedLead] = useState(null);
  const leadsPerPage = 5;
  
  // Fetch leads with pagination
  const { data: leadsData, isLoading: leadsLoading, isValidating: leadsValidating } = useSWR(
    `/v2/dashboard/leads?page=${leadsPage}&limit=${leadsPerPage}`,
    leadsFetcher,
    {
      revalidateOnFocus: false,
      refreshInterval: 120000, // Refresh every 2 minutes
      keepPreviousData: true,
    }
  );
  
  // Copy to clipboard helper
  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${type} copied to clipboard!`);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success(`${type} copied!`);
    }
  };
  
  // Show loading only on initial load (no data yet), not on revalidation
  const showLoading = isLoading && !stats;

  // Format currency using org's currency setting with 2 decimal places
  const formatCurrency = (amount, showPlus = false) => {
    const currencyCode = stats?.currency || 'USD';
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
    return showPlus && amount > 0 ? `+${formatted}` : formatted;
  };

  // Get currency symbol for inline display
  const getCurrencySymbol = () => stats?.currencySymbol || '$';

  const formatDateRange = () => {
    if (!stats?.dateRange?.start) return null;
    
    const formatDate = (dateStr) => {
      const date = new Date(dateStr + 'T00:00:00');
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };
    
    const start = formatDate(stats.dateRange.start);
    const end = formatDate(stats.dateRange.end);
    
    if (start === end) {
      return start;
    }
    return `${start} - ${end}`;
  };

  const handleExport = async (format) => {
    try {
      if (format === 'csv') {
        // For CSV, we need to trigger a download - use v2 endpoint
        const response = await api.get(`/v2/dashboard/export?format=csv&startDate=${startDate}&endDate=${endDate}`, {
          responseType: 'blob'
        });
        
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bookings_${startDate}_to_${endDate}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('CSV exported successfully!');
      } else if (format === 'pdf') {
        // PDF export - opens HTML report that can be printed to PDF
        const response = await api.get(`/v2/dashboard/export?format=pdf&startDate=${startDate}&endDate=${endDate}`, {
          responseType: 'blob'
        });
        
        const blob = new Blob([response.data], { type: 'text/html' });
        const url = window.URL.createObjectURL(blob);
        
        // Open in new window for print-to-PDF
        const printWindow = window.open(url, '_blank');
        if (printWindow) {
          printWindow.onload = () => {
            setTimeout(() => {
              printWindow.print();
            }, 500);
          };
        }
        
        toast.success('PDF report opened! Use browser print (Ctrl+P) to save as PDF');
      } else {
        // JSON export - use v2 endpoint
        const response = await api.get(`/v2/dashboard/export?format=json&startDate=${startDate}&endDate=${endDate}`);
        
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `dashboard_${startDate}_to_${endDate}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('JSON exported successfully!');
      }
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export data');
    }
  };

  // Export dropdown for actions
  const exportActions = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="flex items-center gap-2">
          <Download className="w-4 h-4" />
          Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => handleExport('pdf')} className="cursor-pointer">
          <FileText className="w-4 h-4 mr-2" />
          Export as PDF
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExport('csv')} className="cursor-pointer">
          <FileSpreadsheet className="w-4 h-4 mr-2" />
          Export as CSV
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExport('json')} className="cursor-pointer">
          <FileJson className="w-4 h-4 mr-2" />
          Export as JSON
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <TabPageLayout
      title="Dashboard"
      subtitle="Your operations at a glance"
      loading={showLoading}
      actions={
        <div className="hidden sm:block">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="flex items-center gap-1.5 text-gray-500 hover:text-gray-700">
                <Download className="w-4 h-4" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport('pdf')} className="cursor-pointer">
                <FileText className="w-4 h-4 mr-2" />
                Export as PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('csv')} className="cursor-pointer">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Export as CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('json')} className="cursor-pointer">
                <FileJson className="w-4 h-4 mr-2" />
                Export as JSON
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      }
    >
      <div className="space-y-4 sm:space-y-6">
        {/* Date Range + Stats */}
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            min={dateLimits.minDate}
            max={endDate}
            className="flex-1 sm:flex-none sm:w-[140px] px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white shadow-[0_4px_12px_rgba(0,0,0,0.06)]"
          />
          <span className="text-gray-400 text-sm">to</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            min={startDate}
            max={dateLimits.maxDate}
            className="flex-1 sm:flex-none sm:w-[140px] px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white shadow-[0_4px_12px_rgba(0,0,0,0.06)]"
          />
          {!isDefaultDateRange && (
            <Button variant="ghost" size="sm" onClick={resetDateRange} className="text-gray-500 hover:text-gray-700 px-2" title="Reset">
              <RotateCcw className="w-4 h-4" />
            </Button>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
            {error?.message || 'Failed to load dashboard data'}
          </div>
        )}

        {/* Custom Requests Banner - Only show if there are custom requests */}
        {stats?.customRequests?.count > 0 && (
          <div 
            className="bg-gradient-to-r from-blue-50 to-sky-50 border border-blue-200 rounded-xl p-4 cursor-pointer transition-all hover:shadow-md"
            onClick={() => onNavigate('relations', { filter: 'custom_request' })}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-blue-800">
                    {stats.customRequests.count} Custom Request{stats.customRequests.count !== 1 ? 's' : ''} Pending
                  </p>
                  <p className="text-xs text-blue-600">
                    Customers looking for something special - reach out to close the deal
                  </p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-blue-400" />
            </div>
          </div>
        )}
        {/* Revenue Trend Chart */}
        <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-[0_8px_24px_rgba(0,0,0,0.04)]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Revenue</h3>
            <div className="flex items-center gap-3 text-xs">
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded" style={{ backgroundColor: '#2563EB' }}></div>
                <span className="text-gray-400">Completed</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded" style={{ backgroundColor: '#93b4f7', opacity: 0.6 }}></div>
                <span className="text-gray-400">Collected</span>
              </div>
            </div>
          </div>
          <div className="h-48 sm:h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={stats?.charts?.revenueTrend || []} 
                margin={{ top: 5, right: 5, left: -10, bottom: 5 }}
              >
                <XAxis 
                  dataKey="label" 
                  axisLine={false} 
                  tickLine={false} 
                  interval={0}
                  tick={(props) => (
                    <CustomXAxisTick 
                      {...props} 
                      granularity={stats?.charts?.granularity || 'monthly'} 
                      isMobile={window.innerWidth < 640}
                      dataLength={stats?.charts?.revenueTrend?.length || 0}
                      startDate={startDate}
                      endDate={endDate}
                      isDark={false}
                    />
                  )}
                  height={28}
                />
                <YAxis 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ fontSize: 10, fill: '#94a3b8' }}
                  tickFormatter={(value) => `${getCurrencySymbol()}${value >= 1000 ? Math.round(value/1000) + 'k' : value}`}
                  width={45}
                />
                <Tooltip content={<CustomTooltip currencySymbol={getCurrencySymbol()} isDark={false} />} />
                {/* Stacked bars: Completed on bottom, Projected on top */}
                <Bar 
                  dataKey="completed" 
                  stackId="revenue"
                  shape={<CompletedBarShape />}
                  maxBarSize={40}
                  name="Completed"
                />
                <Bar 
                  dataKey="projected" 
                  stackId="revenue"
                  fill="#93b4f7"
                  fillOpacity={0.6}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={40}
                  name="Projected"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Stats Cards - 4 cards with clean white theme and custom shadow */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {/* Total Revenue Card */}
          <div className="bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.04)] p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-500">Net Revenue</p>
                <p className="text-base sm:text-lg font-bold text-blue-600 truncate">
                  {formatCurrency((stats?.revenue?.total || 0) + (stats?.projected?.total || 0))}
                </p>
              </div>
            </div>
            <div className="text-xs text-gray-400 mt-2 hidden sm:block">
              {(stats?.projected?.total || 0) > 0 ? (
                <p>{formatCurrency(stats?.revenue?.total || 0)} completed • {formatCurrency(stats?.projected?.total || 0)} projected</p>
              ) : (
                <p>{stats?.bookings?.delivered || 0} completed bookings</p>
              )}
            </div>
          </div>

          {/* Bookings Card */}
          <div className="bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.04)] p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                <Calendar className="w-4 h-4 sm:w-5 sm:5 text-indigo-500" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-500">Bookings</p>
                <p className="text-base sm:text-lg font-bold text-indigo-600 truncate">
                  {stats?.bookings?.total || 0}
                </p>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2 hidden sm:block">
              {stats?.bookings?.delivered || 0} completed • {stats?.bookings?.scheduled || 0} upcoming
            </p>
          </div>

          {/* Avg Booking Value */}
          <div className="bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.04)] p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-500">Avg Value</p>
                <p className="text-base sm:text-lg font-bold text-gray-900 truncate">
                  {formatCurrency(stats?.avgBookingValue || 0)}
                </p>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2 hidden sm:block">Per transaction</p>
          </div>

          {/* Leads Card - Uses dedicated leads endpoint for accuracy */}
          <div 
            className={`bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.04)] p-3 sm:p-4 cursor-pointer transition-all hover:shadow-[0_12px_32px_rgba(0,0,0,0.08)] ${
              leadsData?.stats?.new > 0 ? 'ring-1 ring-amber-200' : ''
            }`}
            onClick={() => onNavigate('relations', { filter: 'lead' })}
          >
            <div className="flex items-center gap-2 sm:gap-3">
              <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center relative ${
                leadsData?.stats?.new > 0 ? 'bg-amber-100' : 'bg-gray-100'
              }`}>
                <Users className={`w-4 h-4 sm:w-5 sm:h-5 ${
                  leadsData?.stats?.new > 0 ? 'text-amber-600' : 'text-gray-400'
                }`} />
                {/* New leads notification dot */}
                {leadsData?.stats?.new > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                    <span className="text-[9px] font-bold text-white">{leadsData.stats.new > 9 ? '9+' : leadsData.stats.new}</span>
                  </span>
                )}
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-500">Leads</p>
                <p className={`text-base sm:text-lg font-bold truncate ${
                  leadsData?.pagination?.total > 0 ? 'text-gray-900' : 'text-gray-400'
                }`}>
                  {leadsData?.pagination?.total || 0}
                  {leadsData?.stats?.new > 0 && (
                    <span className="text-amber-600 text-sm ml-1">({leadsData.stats.new} new)</span>
                  )}
                </p>
              </div>
            </div>
            {leadsData?.stats?.new > 0 ? (
              <p className="text-xs text-amber-500 mt-2 hidden sm:block">
                {leadsData.stats.new} new lead{leadsData.stats.new !== 1 ? 's' : ''} waiting →
              </p>
            ) : leadsData?.pagination?.total > 0 ? (
              <p className="text-xs text-gray-500 mt-2 hidden sm:block">All leads contacted ✓</p>
            ) : (
              <p className="text-xs text-gray-400 mt-2 hidden sm:block">No pending leads</p>
            )}
          </div>
        </div>

        {/* Two Column Layout: Upcoming Schedule + Recent Leads */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Upcoming Schedule */}
          <div className="bg-white rounded-xl overflow-hidden flex flex-col shadow-[0_8px_24px_rgba(0,0,0,0.04)]">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-600" />
                <h3 className="font-semibold text-gray-900">Upcoming Schedule</h3>
              </div>
              <button 
                onClick={() => onNavigate('schedule')}
                className="text-xs font-medium text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full hover:bg-blue-100 transition-colors"
              >
                {stats?.upcomingBookings?.length || 0} upcoming →
              </button>
            </div>
            <div className="p-4 flex-1 max-h-[400px] overflow-y-auto">
              {stats?.upcomingBookings?.length > 0 ? (
                <div className="space-y-3">
                  {stats.upcomingBookings.map((booking, i) => {
                    // Format duration nicely
                    const formatDuration = (minutes) => {
                      if (!minutes) return '';
                      if (minutes < 60) return `${minutes} min`;
                      const hours = Math.floor(minutes / 60);
                      const mins = minutes % 60;
                      if (mins === 0) return `${hours}h`;
                      return `${hours}h ${mins}m`;
                    };
                    
                    const isRefunded = booking.paymentStatus === 'refunded' || booking.paymentStatus === 'partially_refunded';
                    
                    return (
                      <div 
                        key={booking.id || i}
                        className={`flex items-center justify-between p-3 rounded-lg transition-colors cursor-pointer ${
                          isRefunded 
                            ? 'bg-red-50 border border-red-100 hover:bg-red-100' 
                            : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                        onClick={() => onNavigate('schedule')}
                        title="View in calendar"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`text-sm font-semibold whitespace-nowrap px-2 py-1 rounded flex flex-col items-center ${
                            isRefunded ? 'text-red-600 bg-red-100' : 'text-blue-600 bg-blue-50'
                          }`}>
                            <span>{booking.date ? new Date(booking.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</span>
                            <span className="text-xs">{formatTime(booking.time)}</span>
                          </div>
                          <div className="min-w-0">
                            <p className={`font-medium text-sm truncate ${isRefunded ? 'text-red-700 line-through' : 'text-gray-900'}`}>
                              {booking.customerName || 'Guest'}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {booking.serviceName}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-2">
                          {isRefunded ? (
                            <span className="text-xs font-medium text-red-600 bg-red-100 px-2 py-0.5 rounded">
                              Refunded
                            </span>
                          ) : (
                            <>
                              {booking.durationMinutes && (
                                <span className="text-xs font-medium text-gray-700 bg-gray-200 px-2 py-0.5 rounded">
                                  {formatDuration(booking.durationMinutes)}
                                </span>
                              )}
                              {booking.daysUntil === 0 ? (
                                <span className="text-xs text-green-600 font-medium">Today</span>
                              ) : booking.daysUntil === 1 ? (
                                <span className="text-xs text-blue-600">Tomorrow</span>
                              ) : (
                                <span className="text-xs text-gray-500">in {booking.daysUntil} days</span>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm font-medium">No upcoming bookings</p>
                  <p className="text-xs text-gray-400 mt-1">Future bookings will appear here</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Column — Recent Leads */}
          <div className="bg-white rounded-xl overflow-hidden flex flex-col shadow-[0_8px_24px_rgba(0,0,0,0.04)] relative" data-testid="leads-section">
            {leadsValidating && leadsData && (
              <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] z-10 flex items-center justify-center">
                <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
              </div>
            )}
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-amber-500" />
                <h3 className="font-semibold text-gray-900 text-sm">Recent Leads</h3>
                {leadsData?.pagination?.total > 0 && (
                  <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">{leadsData.pagination.total}</span>
                )}
              </div>
              <button onClick={() => onNavigate('relations')} className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
                View All <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="flex-1 max-h-[400px] overflow-y-auto">
              {leadsLoading && !leadsData ? (
                <div className="p-8 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                </div>
              ) : leadsData?.leads?.length === 0 ? (
                <div className="p-8 text-center">
                  <UserCircle className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No leads yet</p>
                  <p className="text-xs text-gray-400 mt-1">Leads appear here when customers show interest</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {leadsData?.leads?.map((lead, i) => (
                    <div
                      key={lead.id || i}
                      className="px-3 py-2.5 hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => setSelectedLead(lead)}
                    >
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-[11px] font-semibold">{lead.name?.charAt(0)?.toUpperCase() || '?'}</span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm font-medium text-gray-900 truncate">{lead.name || 'Unknown'}</p>
                            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full flex-shrink-0 uppercase ${
                              lead.status === 'new' ? 'bg-green-100 text-green-700' :
                              lead.status === 'contacted' ? 'bg-blue-100 text-blue-700' :
                              lead.status === 'converted' ? 'bg-purple-100 text-purple-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {lead.isCustomRequest ? 'Custom' : (lead.status || 'new')}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-400">
                            <span className="text-blue-600 truncate">{lead.serviceName}</span>
                            {lead.createdAt && (
                              <span className="flex-shrink-0 hidden sm:inline">
                                · {new Date(lead.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {leadsData?.pagination?.totalPages > 1 && (
              <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50 flex items-center justify-between flex-shrink-0">
                <p className="text-xs text-gray-500">Page {leadsData.pagination.page} of {leadsData.pagination.totalPages}</p>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setLeadsPage(p => Math.max(1, p - 1))} disabled={leadsPage === 1}><ChevronLeft className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setLeadsPage(p => p + 1)} disabled={!leadsData?.pagination?.hasMore}><ChevronRight className="w-4 h-4" /></Button>
                </div>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Lead Detail Modal */}
      <Dialog open={!!selectedLead} onOpenChange={(open) => !open && setSelectedLead(null)}>
        <DialogContent className="sm:max-w-sm p-0 overflow-hidden flex flex-col">
          <DialogTitle className="sr-only">Lead Details</DialogTitle>
          {selectedLead && (
            <>
              {/* Header — clean white, no color */}
              <div className="flex items-center gap-3 px-5 pt-5 pb-4 border-b border-gray-100 flex-shrink-0">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-bold">{selectedLead.name?.charAt(0)?.toUpperCase() || '?'}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-gray-900 truncate">{selectedLead.name || 'Unknown'}</p>
                  <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase mt-0.5 ${
                    selectedLead.status === 'new' ? 'bg-green-100 text-green-700' :
                    selectedLead.status === 'converted' ? 'bg-purple-100 text-purple-700' :
                    selectedLead.status === 'contacted' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {selectedLead.isCustomRequest ? 'Custom Request' : (selectedLead.status || 'new')}
                  </span>
                </div>
              </div>

              {/* Details — scrollable */}
              <div className="flex-1 overflow-y-auto px-5 py-2 min-h-0">
                {[
                  { label: 'Service', value: selectedLead.serviceName },
                  { label: 'Price', value: selectedLead.servicePrice ? `$${Number(selectedLead.servicePrice).toLocaleString()}` : null, blue: true },
                  { label: 'Email', value: selectedLead.email, copy: true },
                  { label: 'Phone', value: selectedLead.phone, copy: true },
                  { label: 'Party Size', value: selectedLead.partySize ? `${selectedLead.partySize} ${selectedLead.partySize === 1 ? 'person' : 'people'}` : null },
                  { label: 'Submitted', value: selectedLead.createdAt ? new Date(selectedLead.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null },
                ].filter(r => r.value).map((row, i) => (
                  <div key={i} className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
                    <span className="text-xs text-gray-400 uppercase tracking-wide flex-shrink-0">{row.label}</span>
                    {row.copy ? (
                      <button onClick={() => copyToClipboard(row.value, row.label)} className="text-sm text-blue-600 hover:underline truncate max-w-[60%] text-right">
                        {row.value}
                      </button>
                    ) : (
                      <span className={`text-sm text-right truncate max-w-[60%] ${row.blue ? 'font-semibold text-blue-600' : 'font-medium text-gray-900'}`}>
                        {row.value}
                      </span>
                    )}
                  </div>
                ))}
                {selectedLead.message && (
                  <div className="py-3">
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-1.5">Message</p>
                    <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 leading-relaxed">{selectedLead.message}</p>
                  </div>
                )}
              </div>

              {/* CTAs — sticky to bottom */}
              <div className="flex gap-2 px-5 py-4 border-t border-gray-100 bg-white sticky bottom-0 flex-shrink-0">
                <Button
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => { setSelectedLead(null); onNavigate('relations'); }}
                >
                  View Full Profile
                </Button>
                <Button variant="outline" className="flex-1" onClick={() => setSelectedLead(null)}>
                  Close
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

    </TabPageLayout>
  );
};

export default OperationsDashboard;
